
import React, { useState } from 'react';

export default function TechShowcaseSection() {
  const [activeTab, setActiveTab] = useState(0);

  const techSolutions = [
    {
      id: 1,
      title: 'AI智能碳管控系统',
      category: '人工智能',
      description: '基于深度学习的碳排放智能分析与预测系统',
      features: [
        '神经网络碳排放建模',
        '实时AI预警系统',
        '智能减排策略推荐',
        '自适应学习优化'
      ],
      metrics: {
        accuracy: '98.5%',
        reduction: '45%',
        efficiency: '3.2x'
      },
      image: 'https://readdy.ai/api/search-image?query=Advanced%20AI%20carbon%20management%20system%20with%20neural%20networks%2C%20machine%20learning%20algorithms%2C%20holographic%20data%20visualization%2C%20futuristic%20control%20interfaces%2C%20AI-powered%20environmental%20monitoring%20dashboards%20with%20real-time%20analytics%20and%20predictive%20modeling&width=800&height=500&seq=tech-showcase-ai-001&orientation=landscape'
    },
    {
      id: 2,
      title: '物联网环境监测网',
      category: '物联网技术',
      description: '全方位IoT传感器网络，实现环境数据的精准采集',
      features: [
        '多维度传感器阵列',
        '5G高速数据传输',
        '边缘计算处理',
        '自组网通信协议'
      ],
      metrics: {
        sensors: '10000+',
        coverage: '99.9%',
        latency: '<10ms'
      },
      image: 'https://readdy.ai/api/search-image?query=IoT%20environmental%20monitoring%20network%20with%20smart%20sensors%2C%20wireless%20communication%20towers%2C%20edge%20computing%20nodes%2C%20real-time%20data%20collection%20systems%2C%20connected%20environmental%20devices%20in%20industrial%20and%20urban%20settings&width=800&height=500&seq=tech-showcase-iot-002&orientation=landscape'
    },
    {
      id: 3,
      title: '区块链碳信用平台',
      category: '区块链技术',
      description: '去中心化碳资产管理与交易生态系统',
      features: [
        '分布式账本技术',
        '智能合约自动执行',
        'NFT碳信用凭证',
        '跨链互操作协议'
      ],
      metrics: {
        transactions: '1M+',
        security: '100%',
        transparency: '完全透明'
      },
      image: 'https://readdy.ai/api/search-image?query=Blockchain%20carbon%20credit%20platform%20with%20cryptocurrency%20networks%2C%20digital%20tokens%2C%20smart%20contracts%2C%20decentralized%20trading%20interfaces%2C%20secure%20blockchain%20nodes%2C%20NFT%20carbon%20certificates%2C%20distributed%20ledger%20visualization&width=800&height=500&seq=tech-showcase-blockchain-003&orientation=landscape'
    }
  ];

  const innovationAreas = [
    {
      icon: 'ri-brain-line',
      title: '机器学习',
      desc: '深度学习算法优化',
      color: 'from-emerald-500 to-green-500'
    },
    {
      icon: 'ri-quantum-line',
      title: '量子计算',
      desc: '复杂碳模型求解',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: 'ri-robot-line',
      title: '自动化控制',
      desc: '智能减排执行',
      color: 'from-purple-500 to-indigo-500'
    },
    {
      icon: 'ri-database-line',
      title: '大数据分析',
      desc: 'PB级数据处理',
      color: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-500 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium mb-4 border border-white/20">
            <i className="ri-rocket-line mr-2"></i>
            科技创新展示
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              前沿环保科技
            </span>
            <br />
            驱动绿色未来
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            运用最先进的技术手段，构建智能化、精准化的碳管理解决方案
          </p>
        </div>

        {/* Tech Solutions Showcase */}
        <div className="mb-20">
          {/* Tab Navigation */}
          <div className="flex justify-center mb-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-2 border border-white/20">
              {techSolutions.map((solution, index) => (
                <button
                  key={solution.id}
                  onClick={() => setActiveTab(index)}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeTab === index
                      ? 'bg-white text-gray-900 shadow-lg'
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {solution.category}
                </button>
              ))}
            </div>
          </div>

          {/* Active Solution Display */}
          <div className="bg-white/5 backdrop-blur-lg rounded-3xl p-8 lg:p-12 border border-white/10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Content */}
              <div>
                <div className="mb-6">
                  <span className="inline-block px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-medium mb-4">
                    {techSolutions[activeTab].category}
                  </span>
                  <h3 className="text-3xl font-bold mb-4">{techSolutions[activeTab].title}</h3>
                  <p className="text-lg text-gray-300 leading-relaxed">
                    {techSolutions[activeTab].description}
                  </p>
                </div>

                {/* Features */}
                <div className="mb-8">
                  <h4 className="text-xl font-semibold mb-4">核心功能</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {techSolutions[activeTab].features.map((feature, index) => (
                      <div key={index} className="flex items-center text-gray-300">
                        <i className="ri-check-line text-emerald-400 mr-3"></i>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-3 gap-6">
                  {Object.entries(techSolutions[activeTab].metrics).map(([key, value], index) => (
                    <div key={index} className="text-center">
                      <div className="text-2xl font-bold text-emerald-400">{value}</div>
                      <div className="text-sm text-gray-400 capitalize">{key.replace(/([A-Z])/g, ' $1')}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Image */}
              <div className="lg:order-first">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={techSolutions[activeTab].image}
                    alt={techSolutions[activeTab].title}
                    className="w-full h-80 lg:h-96 object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Innovation Areas */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4">创新技术领域</h3>
            <p className="text-lg text-gray-300">持续推进前沿技术研发与应用</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {innovationAreas.map((area, index) => (
              <div key={index} className="group">
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/30 transition-all duration-300 hover:transform hover:scale-105">
                  <div className={`w-16 h-16 bg-gradient-to-br ${area.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${area.icon} text-white text-2xl`}></i>
                  </div>
                  <h4 className="text-xl font-bold mb-2">{area.title}</h4>
                  <p className="text-gray-300">{area.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-emerald-600/20 to-cyan-600/20 backdrop-blur-lg rounded-3xl p-8 lg:p-12 border border-emerald-500/30">
            <h3 className="text-3xl font-bold mb-4">体验下一代环保科技</h3>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
              加入我们，共同探索科技与环保融合的无限可能
            </p>
            <button className="px-8 py-4 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white font-semibold rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl transform hover:scale-105">
              <i className="ri-rocket-line mr-2"></i>
              开启智能环保之旅
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
